/**
 * RouteErrorBoundary — v.1-D §4.13 / §7.18.
 *
 * Per-route class boundary. Wraps every routed page via the `<L name="…">`
 * helper in `routes/index.tsx`. Renders a page-level error Panel while the
 * sidebar / header chrome stays intact. Distinct from `AppErrorBoundary`
 * (full-screen 500) and the optional per-Panel `<ErrorBoundary>`.
 */
import React from 'react';
import { logger } from '../../lib/logger';

export interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  routeName?: string;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  message: string;
}

export class RouteErrorBoundary extends React.Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { hasError: false, message: '' };

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error('route_error_boundary_caught', {
      route:   this.props.routeName ?? 'unknown',
      message: error.message,
      stack:   info.componentStack,
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <section
          role="alert"
          style={{
            background:    'var(--panel)',
            border:        '1px solid var(--border-light)',
            borderRadius:  'var(--radius-signature)',
            padding:       'var(--space-9)',
            margin:        'var(--space-8) 0',
            color:         'var(--ink)',
          }}
        >
          <h2 style={{ marginTop: 0, fontFamily: 'var(--font-sans)', fontSize: 24 }}>
            Page error
          </h2>
          <p className="t-caption" style={{ color: 'var(--muted)' }}>
            Something went wrong rendering this page. The rest of the app is unaffected.
          </p>
          <pre
            className="mono-micro"
            style={{
              marginTop:    'var(--space-6)',
              padding:      'var(--space-5)',
              background:   'var(--lightest-gray)',
              borderRadius: 'var(--radius-md)',
              overflow:     'auto',
              fontFamily:   'var(--font-mono)',
            }}
          >
            {this.state.message}
          </pre>
        </section>
      );
    }
    return this.props.children;
  }
}
